import { TechNode } from '../../types';

/**
 * Biotechnology domain nodes
 * Gene editing, longevity, and synthetic biology
 */
export const BIOTECH_NODES: TechNode[] = [
  // === FOUNDATIONAL (Tier 0) ===
  {
    id: 'biotech-genome-sequencing',
    name: 'Genome Sequencing',
    description: 'Reading the complete genetic code of organisms',
    domain: 'biotech',
    status: 'achieved',
    yearAchieved: 2003,
    importance: 9,
    tier: 0,
    trl: 7,
    milestones: [
      {
        date: '2003-04-14',
        title: 'Human Genome Project Complete',
        description: 'First complete human genome sequenced',
        significance: 'breakthrough',
        source: 'https://www.genome.gov/11006929/2003-release-international-consortium-completes-hgp',
      },
      {
        date: '2008-01-01',
        title: 'Next-Gen Sequencing',
        description: 'Illumina and others drive cost below $1M per genome',
        significance: 'major',
        source: 'https://www.nature.com/articles/nature07517',
      },
      {
        date: '2022-03-31',
        title: 'Telomere-to-Telomere',
        description: 'First complete gapless human genome assembly',
        significance: 'major',
        source: 'https://www.science.org/doi/10.1126/science.abj6987',
      },
    ],
  },
  {
    id: 'biotech-crispr',
    name: 'CRISPR Gene Editing',
    description: 'Precise, programmable DNA editing technology',
    domain: 'biotech',
    status: 'achieved',
    yearAchieved: 2012,
    importance: 10,
    tier: 0,
    trl: 8,
    keyPlayers: ['Jennifer Doudna', 'Emmanuelle Charpentier', 'Feng Zhang'],
    milestones: [
      {
        date: '2012-06-28',
        title: 'CRISPR-Cas9 Paper',
        description: 'Demonstration of programmable genome editing',
        significance: 'breakthrough',
        source: 'https://www.science.org/doi/10.1126/science.1225829',
      },
      {
        date: '2020-10-07',
        title: 'Nobel Prize',
        description: 'Doudna and Charpentier awarded for CRISPR-Cas9',
        significance: 'major',
        source: 'https://www.nobelprize.org/prizes/chemistry/2020/press-release/',
      },
      {
        date: '2021-06-26',
        title: 'First In Vivo CRISPR',
        description: 'Intellia/Regeneron CRISPR edit in humans (transthyretin)',
        significance: 'major',
        source: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2107454',
      },
    ],
  },

  // === CURRENT APPLICATIONS (Tier 1) ===
  {
    id: 'biotech-gene-therapy',
    name: 'Gene Therapy',
    description: 'Treating diseases by modifying patient genes',
    domain: 'biotech',
    status: 'in_progress',
    importance: 9,
    tier: 1,
    trl: 6,
    dependencies: ['biotech-crispr'],
    keyPlayers: ['Vertex', 'CRISPR Therapeutics', 'Editas'],
    progress: 55,
    milestones: [
      {
        date: '2023-12-08',
        title: 'Casgevy Approved',
        description: 'First CRISPR therapy approved for sickle cell disease',
        significance: 'breakthrough',
        source: 'https://www.fda.gov/news-events/press-announcements/fda-approves-first-gene-therapies-treat-patients-sickle-cell-disease',
      },
      {
        date: '2017-12-19',
        title: 'Luxturna Approved',
        description: 'First gene therapy approved in US (RPE65 inherited blindness)',
        significance: 'major',
        source: 'https://www.fda.gov/vaccines-blood-biologics/cellular-gene-therapy-products/luxturna',
      },
      {
        date: '2019-05-24',
        title: 'Zolgensma Approved',
        description: 'FDA approves gene therapy for spinal muscular atrophy in young children',
        significance: 'major',
        source: 'https://www.fda.gov/vaccines-blood-biologics/zolgensma',
      },
    ],
  },
  {
    id: 'biotech-mrna',
    name: 'mRNA Therapeutics',
    description: 'Using mRNA to produce therapeutic proteins',
    domain: 'biotech',
    status: 'achieved',
    yearAchieved: 2020,
    importance: 8,
    tier: 1,
    trl: 9,
    keyPlayers: ['Moderna', 'BioNTech', 'Pfizer'],
    progress: 100,
    milestones: [
      {
        date: '2020-12-11',
        title: 'First COVID mRNA Vaccine',
        description: 'FDA EUA for Pfizer-BioNTech COVID-19 vaccine',
        significance: 'breakthrough',
        source: 'https://www.fda.gov/news-events/press-announcements/fda-takes-key-action-fight-against-covid-19-issuing-emergency-use-authorization-first-covid-19',
      },
      {
        date: '2023-08-21',
        title: 'Moderna Flu Vaccine',
        description: 'First mRNA flu vaccine positive Phase 3 results',
        significance: 'major',
        source: 'https://investors.modernatx.com/news/news-details/2023/Moderna-Announces-mRNA-1010-Met-Primary-Efficacy-Endpoint-in-Phase-3-Trial-Against-Influenza/default.aspx',
      },
    ],
  },
  {
    id: 'biotech-synthetic-biology',
    name: 'Synthetic Biology',
    description: 'Engineering organisms with novel capabilities',
    domain: 'biotech',
    status: 'in_progress',
    importance: 8,
    tier: 1,
    trl: 5,
    dependencies: ['biotech-crispr'],
    keyPlayers: ['Ginkgo Bioworks', 'Zymergen', 'Twist Bioscience'],
    progress: 50,
    milestones: [
      {
        date: '2010-05-20',
        title: 'First Synthetic Cell',
        description: 'Venter institute creates bacterium with synthetic genome',
        significance: 'breakthrough',
        source: 'https://www.science.org/doi/10.1126/science.1190719',
      },
      {
        date: '2021-09-17',
        title: 'Ginkgo SPAC',
        description: 'Ginkgo Bioworks goes public; cell programming at scale',
        significance: 'major',
        source: 'https://investors.ginkgobioworks.com/news/news-details/2021/Ginkgo-Bioworks-Begins-Trading-on-NYSE/default.aspx',
      },
    ],
  },

  // === LONGEVITY (Tier 2-3) ===
  {
    id: 'biotech-senolytics',
    name: 'Senolytics',
    description: 'Drugs that clear senescent cells to slow aging',
    domain: 'biotech',
    status: 'in_progress',
    importance: 8,
    tier: 2,
    trl: 5,
    keyPlayers: ['Unity Biotechnology', 'Oisín Biotechnologies'],
    progress: 35,
    milestones: [
      {
        date: '2016-02-03',
        title: 'Senolytic Extension of Lifespan',
        description: 'Baker et al.: clearing p16-positive senescent cells extends healthspan in mice (Nature)',
        significance: 'major',
        source: 'https://www.nature.com/articles/nature16932',
      },
      {
        date: '2024-01-01',
        title: 'Unity UBX1325',
        description: 'Senolytic in trials for age-related macular degeneration',
        significance: 'minor',
        source: 'https://www.unitybiotechnology.com/pipeline/',
      },
    ],
  },
  {
    id: 'biotech-epigenetic-reprogramming',
    name: 'Epigenetic Reprogramming',
    description: 'Resetting cellular age markers',
    domain: 'biotech',
    status: 'in_progress',
    importance: 9,
    tier: 2,
    trl: 4,
    keyPlayers: ['Altos Labs', 'Retro Bio', 'NewLimit'],
    progress: 25,
    milestones: [
      {
        date: '2016-12-15',
        title: 'Yamanaka In Vivo',
        description: 'Partial reprogramming reverses aging signs in mice (Izpisúa Belmonte)',
        significance: 'breakthrough',
        source: 'https://www.cell.com/cell/fulltext/S0092-8674(16)31664-6',
      },
      {
        date: '2022-01-19',
        title: 'Altos Labs Launch',
        description: '$3B to reverse disease and extend healthspan via cellular reprogramming',
        significance: 'major',
        source: 'https://www.altoslabs.com/news/altos-labs-launches-with-mission-to-restore-cell-health-and-reversibility',
      },
      {
        date: '2023-01-12',
        title: 'Partial Reprogramming in Mice',
        description: 'Extended lifespan via Yamanaka factors (Lu et al.)',
        significance: 'major',
        source: 'https://www.cell.com/cell/fulltext/S0092-8674(22)01570-7',
      },
    ],
  },
  {
    id: 'biotech-longevity-escape',
    name: 'Longevity Escape Velocity',
    description: 'Life extension exceeding rate of aging',
    domain: 'biotech',
    status: 'theoretical',
    importance: 10,
    tier: 4,
    trl: 3,
    dependencies: ['biotech-senolytics', 'biotech-epigenetic-reprogramming'],
    yearEstimated: 2050,
  },

  // === ADVANCED (Tier 3+) ===
  {
    id: 'biotech-organ-printing',
    name: '3D Bioprinted Organs',
    description: 'Printing functional organs from patient cells',
    domain: 'biotech',
    status: 'in_progress',
    importance: 8,
    tier: 3,
    trl: 4,
    keyPlayers: ['Organovo', 'BIOLIFE4D', 'CollPlant'],
    progress: 30,
    yearEstimated: 2030,
    milestones: [
      {
        date: '2013-04-10',
        title: 'First Bioprinted Organ',
        description: 'Organovo prints miniature human liver tissue',
        significance: 'major',
        source: 'https://www.organovo.com/2013/04/10/organovo-announces-successful-3d-bioprinting-of-liver-tissue/',
      },
      {
        date: '2024-01-01',
        title: 'Bioprinted Tissue Trials',
        description: 'Multiple companies in preclinical or early clinical bioprinted tissue',
        significance: 'minor',
        source: 'https://www.nature.com/articles/s41536-021-00145-z',
      },
    ],
  },
  {
    id: 'biotech-xenotransplant',
    name: 'Xenotransplantation',
    description: 'Transplanting organs from modified animals to humans',
    domain: 'biotech',
    status: 'in_progress',
    importance: 7,
    tier: 2,
    trl: 6,
    dependencies: ['biotech-crispr'],
    keyPlayers: ['eGenesis', 'United Therapeutics'],
    progress: 45,
    milestones: [
      {
        date: '2022-01-07',
        title: 'Pig Heart Transplant',
        description: 'First genetically modified pig heart in human (University of Maryland)',
        significance: 'breakthrough',
        source: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2121421',
      },
      {
        date: '2024-03-21',
        title: 'Pig Kidney Transplant',
        description: 'First genetically modified pig kidney in living patient',
        significance: 'breakthrough',
        source: 'https://www.massgeneral.org/news/press-release/first-successful-transplant-of-a-genetically-modified-pig-kidney-into-a-living-human',
      },
    ],
  },
  {
    id: 'biotech-de-extinction',
    name: 'De-Extinction',
    description: 'Bringing back extinct species via genetic engineering',
    domain: 'biotech',
    status: 'in_progress',
    importance: 6,
    tier: 3,
    trl: 4,
    dependencies: ['biotech-synthetic-biology', 'biotech-crispr'],
    keyPlayers: ['Colossal Biosciences'],
    progress: 15,
    milestones: [
      {
        date: '2021-09-13',
        title: 'Colossal Launch',
        description: 'Colossal Biosciences founded to resurrect woolly mammoth',
        significance: 'major',
        source: 'https://colossal.com/colossal-biosciences-launches-with-15m-to-bring-back-the-woolly-mammoth/',
      },
      {
        date: '2022-08-16',
        title: 'Thylacine Gene Editing',
        description: 'Colossal and UMelbourne progress toward de-extinction of thylacine',
        significance: 'minor',
        source: 'https://colossal.com/thylacine-de-extinction/',
      },
    ],
  },
];

export default BIOTECH_NODES;
